import { useAppState } from '../../../client/providers/AppProvider';
import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import {
    NeedsOnboardingInformation,
    useAuthenticatedMutation,
    useAuthenticatedQuery,
    usePageRequireAuth,
} from '../../../client/hooks';
import { DashboardFullPageLoading } from '../../../components/common/GenericFullPageLoading';
import { DashboardLayout } from '../../../components/common/layout/DashboardLayout';
import { MessagesCard } from '../../../components/dashboard/messages/MessagesCard';
import {
    Espin_Role_Enum,
    CollegeMessagesDocument,
    PlayerMessagesDocument,
    ParentMessagesDocument,
    MessagesCardFragment,
    RecordMessageViewsMutation,
    RecordMessageViewsMutationVariables,
    RecordMessageViewsDocument,
    Espin_Message_Type_Enum,
    CollegeMessagesQuery,
    CollegeMessagesQueryVariables,
    PlayerMessagesQuery,
    PlayerMessagesQueryVariables,
    ParentMessagesQuery,
    ParentMessagesQueryVariables,
    Espin_Message_Bool_Exp,
    SearchPlayerMessagesQuery,
    SearchPlayerMessagesQueryVariables,
    SearchPlayerMessagesDocument,
    ConversationWithCollegeBySlugQuery,
    ConversationWithCollegeBySlugQueryVariables,
    ConversationWithCollegeBySlugDocument,
    ConversationWithPlayerBySlugQuery,
    ConversationWithPlayerBySlugQueryVariables,
    ConversationWithPlayerBySlugDocument,
    RecordAdminPlayerMessageViewsMutation,
    RecordAdminPlayerMessageViewsMutationVariables,
    RecordAdminPlayerMessageViewsDocument,
    RecordAdminCollegeMessageViewsMutation,
    RecordAdminCollegeMessageViewsMutationVariables,
    RecordAdminCollegeMessageViewsDocument,
    HighSchoolMessagesQuery,
    HighSchoolMessagesQueryVariables,
    HighSchoolMessagesDocument,
    ConversationWithHighSchoolBySlugQuery,
    ConversationWithHighSchoolBySlugQueryVariables,
    ConversationWithHighSchoolBySlugDocument,
    ChildProfileDataQuery,
    ChildProfileDataQueryVariables,
    ChildProfileDataDocument,
} from '../../../generated/spin-graphql';
import { ParentMessages } from '../../../components/dashboard/messages/parents/ParentMessages';
import { gql } from '@urql/core';
import { Query, useClient } from 'urql';
import { useRouter } from 'next/router';
import { useImmerReducer } from 'use-immer';
import { InitialMessageContent } from '../../../components/dashboard/messages/InitialConversationContent';

gql`
    query CollegeMessages($collegeId: uuid) {
        espin_message(where: { college_id: { _eq: $collegeId } }, order_by: { created_at: asc }) {
            ...MessagesCard
        }
    }

    query HighSchoolMessages($highSchoolId: uuid) {
        espin_message(where: { high_school_id: { _eq: $highSchoolId } }, order_by: { created_at: asc }) {
            ...MessagesCard
        }
    }

    query SearchPlayerMessages($playerSearch: espin_message_bool_exp!) {
        espin_message(where: $playerSearch, order_by: { created_at: asc }) {
            ...MessagesCard
        }
    }

    query PlayerMessages($playerUserId: String) {
        espin_message(where: { player_user_id: { _eq: $playerUserId } }, order_by: { created_at: asc }) {
            ...MessagesCard
        }
    }

    query ParentMessages($playerUserIds: [String!]) {
        espin_message(where: { player_user_id: { _in: $playerUserIds } }, order_by: { created_at: asc }) {
            ...MessagesCard
        }
    }

    query ChildProfileData($id: String!) {
        espin_parent_profile(where: { user_id: { _eq: $id } }) {
            ...ParentChildDropdown
        }
    }

    query ConversationWithCollegeBySlug($slug: String! = "") {
        collegeIdBySlug: espin_college(where: { slug: { _eq: $slug } }) {
            id
            name
            logo {
                ...SpinContentImageDisplay
            }
            slug
        }
    }

    query ConversationWithHighSchoolBySlug($slug: String! = "") {
        highSchoolIdBySlug: espin_high_school(where: { slug: { _eq: $slug } }) {
            id
            name
            profile_pic_file {
                ...SpinContentImageDisplay
            }
            slug
        }
    }

    query ConversationWithPlayerBySlug($slug: String! = "") {
        playerIdBySlug: espin_player_profile(where: { slug: { _eq: $slug } }) {
            user_id
            first_name
            last_name
            slug
            profile_image {
                ...SpinContentImageDisplay
            }
        }
    }

    mutation RecordMessageViews(
        $collegeId: uuid = ""
        $playerUserId: String = ""
        $messageType: espin_message_type_enum!
    ) {
        update_espin_message(
            where: {
                player_user_id: { _eq: $playerUserId }
                college_id: { _eq: $collegeId }
                message_type: { _eq: $messageType }
                viewed_at: { _is_null: true }
            }
            _set: { viewed_at: "now()" }
        ) {
            affected_rows
        }
    }

    mutation RecordAdminPlayerMessageViews($playerUserId: String!, $messageType: espin_message_type_enum!) {
        update_espin_message(
            where: {
                player_user_id: { _eq: $playerUserId }
                message_type: { _eq: $messageType }
                viewed_at: { _is_null: true }
            }
            _set: { viewed_at: "now()" }
        ) {
            affected_rows
        }
    }

    mutation RecordAdminCollegeMessageViews($collegeId: uuid!, $messageType: espin_message_type_enum!) {
        update_espin_message(
            where: {
                college_id: { _eq: $collegeId }
                message_type: { _eq: $messageType }
                viewed_at: { _is_null: true }
            }
            _set: { viewed_at: "now()" }
        ) {
            affected_rows
        }
    }
`;

export type Conversations = {
    [user_id: string]: MessagesCardFragment[];
};

export default function MessagesPage() {
    const { user, subscribed, getAccessTokenSilently } = useAppState();
    const router = useRouter();
    const gqlClient = useClient();

    const { data: collegeMessages } = useAuthenticatedQuery<CollegeMessagesQuery, CollegeMessagesQueryVariables>(
        {
            query: CollegeMessagesDocument,
            variables: useMemo(() => ({ collegeId: user?.college_profile?.college?.id }), [user]),
            pause: !!!user?.college_profile?.college,
        },
        ['espin_message'],
    );

    const { data: highSchoolMessages } = useAuthenticatedQuery<
        HighSchoolMessagesQuery,
        HighSchoolMessagesQueryVariables
    >(
        {
            query: HighSchoolMessagesDocument,
            variables: useMemo(() => ({ highSchoolId: user?.high_school_profile?.high_school?.id }), [user]),
            pause: !!!user?.high_school_profile?.high_school,
        },
        ['espin_message'],
    );

    const { data: playerMessages } = useAuthenticatedQuery<PlayerMessagesQuery, PlayerMessagesQueryVariables>(
        {
            query: PlayerMessagesDocument,
            variables: useMemo(() => ({ playerUserId: user?.id }), [user]),
            pause: !!!user?.player_profile,
        },
        ['espin_message'],
    );

    const { data: parentMessages } = useAuthenticatedQuery<ParentMessagesQuery, ParentMessagesQueryVariables>(
        {
            query: ParentMessagesDocument,
            variables: useMemo(() => ({ playerUserIds: user?.parent_profile?.child_profiles.map((i) => i.user_id) }), [
                user,
            ]),
            pause: !!!user?.parent_profile?.child_profiles,
        },
        ['espin_message'],
    );

    const { data: collegeConversationData, isError } = useAuthenticatedQuery<
        ConversationWithCollegeBySlugQuery,
        ConversationWithCollegeBySlugQueryVariables
    >(
        {
            query: ConversationWithCollegeBySlugDocument,
            variables: useMemo(() => ({ slug: router.query.college_slug?.toString() }), [router.query.college_slug]),
            pause: !!!router.query.college_slug,
        },
        ['espin_message'],
    );

    const { data: childProfileData } = useAuthenticatedQuery<ChildProfileDataQuery, ChildProfileDataQueryVariables>(
        {
            query: ChildProfileDataDocument,
            variables: useMemo(() => ({ id: user?.id }), [user?.id]),
            pause:
                (user?.role === Espin_Role_Enum.EspinParentUser && !!!user?.parent_profile?.child_profiles) ||
                (user?.role === Espin_Role_Enum.EspinPlayerUser && !!!user?.player_profile),
        },
        ['espin_player_profile'],
    );

    const { data: highSchoolConversationData } = useAuthenticatedQuery<
        ConversationWithHighSchoolBySlugQuery,
        ConversationWithHighSchoolBySlugQueryVariables
    >({
        query: ConversationWithHighSchoolBySlugDocument,
        variables: { slug: router.query.high_school_slug?.toString() },
        pause: !!!router.query.high_school_slug,
    });

    const { data: playerConversationData } = useAuthenticatedQuery<
        ConversationWithPlayerBySlugQuery,
        ConversationWithPlayerBySlugQueryVariables
    >({
        query: ConversationWithPlayerBySlugDocument,
        variables: useMemo(() => ({ slug: router.query.player_slug?.toString() }), [router.query.player_slug]),
        pause: !!!router.query.player_slug,
    });

    const buildMessageSearchCritiria = useCallback(
        (searchText: string) => {
            let termSearch: Espin_Message_Bool_Exp | null = null;
            if (searchText !== '') {
                termSearch = {
                    _or: [],
                };
                if (user?.role === Espin_Role_Enum.EspinCollegeUser) {
                    termSearch.college_id = { _eq: user?.college_profile?.college?.id };
                    termSearch._or.push({
                        player_user: { player_profile: { first_name: { _ilike: `%${searchText}%` } } },
                    });
                    termSearch._or.push({
                        player_user: { player_profile: { last_name: { _ilike: `%${searchText}%` } } },
                    });
                    termSearch._or.push({ player_user: { player_profile: { slug: { _ilike: `%${searchText}%` } } } });
                } else if (user?.role === Espin_Role_Enum.EspinPlayerUser) {
                    termSearch.player_user_id = {
                        _eq: user?.id,
                    };
                    termSearch._or.push({
                        college_user: { college_profile: { first_name: { _ilike: `%${searchText}%` } } },
                    });
                    termSearch._or.push({
                        college_user: { college_profile: { last_name: { _ilike: `%${searchText}%` } } },
                    });
                    termSearch._or.push({ college: { program_name: { _ilike: `%${searchText}%` } } });
                } else if (user?.role === Espin_Role_Enum.EspinParentUser) {
                    termSearch.player_user_id = {
                        _in: user?.parent_profile?.child_profiles.map((i) => i.user_id),
                    };
                    termSearch._or.push({
                        college_user: { college_profile: { first_name: { _ilike: `%${searchText}%` } } },
                    });
                    termSearch._or.push({
                        college_user: { college_profile: { last_name: { _ilike: `%${searchText}%` } } },
                    });
                    termSearch._or.push({ college: { program_name: { _ilike: `%${searchText}%` } } });
                }
                return termSearch;
            }
            return {};
        },
        [user?.college_profile?.college?.id, user?.id, user?.parent_profile?.child_profiles, user?.role],
    );

    const [_, recordMessageViews] = useAuthenticatedMutation<
        RecordMessageViewsMutation,
        RecordMessageViewsMutationVariables
    >(RecordMessageViewsDocument);

    const [__, recordAdminPlayerMessageViews] = useAuthenticatedMutation<
        RecordAdminPlayerMessageViewsMutation,
        RecordAdminPlayerMessageViewsMutationVariables
    >(RecordAdminPlayerMessageViewsDocument);

    const [___, recordAdminCollegeMessageViews] = useAuthenticatedMutation<
        RecordAdminCollegeMessageViewsMutation,
        RecordAdminCollegeMessageViewsMutationVariables
    >(RecordAdminCollegeMessageViewsDocument);

    const messages: MessagesCardFragment[] = useMemo(() => {
        if (user?.role === Espin_Role_Enum.EspinCollegeUser) {
            return collegeMessages?.espin_message ?? [];
        } else if (user?.role === Espin_Role_Enum.EspinPlayerUser) {
            return playerMessages?.espin_message ?? [];
        } else if (user?.role === Espin_Role_Enum.EspinParentUser) {
            return parentMessages?.espin_message ?? [];
        } else if (user?.role === Espin_Role_Enum.EspinHighschoolUser) {
            return highSchoolMessages?.espin_message ?? [];
        } else {
            return [];
        }
    }, [
        user?.role,
        collegeMessages?.espin_message,
        playerMessages?.espin_message,
        parentMessages?.espin_message,
        highSchoolMessages?.espin_message,
    ]);

    const [messagesState, messagesDispatch] = useImmerReducer<MessagesReducerState, MessagesReducerAction>(
        messagesReducer,
        {
            selectedIndex: 0,
            fullConversationList: {},
            filteredConversationList: [],
            searchTermString: '',
            selectedChildId: '',
        },
    );
    const { filteredConversationList } = messagesState;

    let initialMessageContent: InitialMessageContent = useMemo(() => {
        if (
            !!!router.query.back_to_inbox &&
            playerConversationData?.playerIdBySlug.length > 0 &&
            !Object.keys(filteredConversationList).includes(playerConversationData?.playerIdBySlug[0].user_id)
        ) {
            return {
                player_user_id: playerConversationData?.playerIdBySlug[0].user_id,
                college_id: user?.college_profile?.college?.id,
                college_user_id: user?.id,
                slug: playerConversationData?.playerIdBySlug[0].slug,
                conversantName: `${playerConversationData?.playerIdBySlug[0].first_name} ${playerConversationData?.playerIdBySlug[0].last_name}`,
                profileImage: playerConversationData?.playerIdBySlug[0].profile_image,
            };
        } else if (
            !!!router.query.back_to_inbox &&
            collegeConversationData?.collegeIdBySlug.length > 0 &&
            !Object.keys(filteredConversationList).includes(collegeConversationData?.collegeIdBySlug[0].id)
        ) {
            return {
                player_user_id:
                    user?.role === Espin_Role_Enum.EspinParentUser
                        ? childProfileData[0]?.child_profiles[0]?.user_id
                        : user?.id,
                ...(!!user && user.role === Espin_Role_Enum.EspinParentUser && { parent_user_id: user.id }),
                high_school_id: user?.high_school_profile?.high_school?.id,
                college_id: collegeConversationData?.collegeIdBySlug[0].id,
                slug: collegeConversationData?.collegeIdBySlug[0].slug,
                conversantName: collegeConversationData?.collegeIdBySlug[0].name,
                profileImage: collegeConversationData?.collegeIdBySlug[0].logo,
            };
        } else if (
            !!!router.query.back_to_inbox &&
            highSchoolConversationData?.highSchoolIdBySlug.length > 0 &&
            !Object.keys(filteredConversationList).includes(highSchoolConversationData?.highSchoolIdBySlug[0].id)
        ) {
            return {
                high_school_id: highSchoolConversationData?.highSchoolIdBySlug[0].id,
                college_user_id: user?.id,
                college_id: user?.college_profile?.college?.id,
                slug: highSchoolConversationData?.highSchoolIdBySlug[0].slug,
                conversantName: highSchoolConversationData?.highSchoolIdBySlug[0].name,
                profileImage: highSchoolConversationData?.highSchoolIdBySlug[0].profile_pic_file,
            };
        } else {
            return undefined;
        }
    }, [
        highSchoolConversationData,
        collegeConversationData,
        router.query.back_to_inbox,
        playerConversationData?.playerIdBySlug,
        filteredConversationList,
        user,
        childProfileData,
    ]);

    const adminMessages: MessagesCardFragment[] = useMemo(() => {
        if (user?.role === Espin_Role_Enum.EspinPlayerUser) {
            return (
                playerMessages?.espin_message?.filter(
                    (m) => m.message_type === Espin_Message_Type_Enum.AdminMessagesPlayer,
                ) ?? []
            );
        } else if (user?.role === Espin_Role_Enum.EspinParentUser) {
            return (
                parentMessages?.espin_message?.filter(
                    (m) => m.message_type === Espin_Message_Type_Enum.AdminMessagesPlayer,
                ) ?? []
            );
        } else if (user?.role === Espin_Role_Enum.EspinCollegeUser) {
            return (
                collegeMessages?.espin_message?.filter(
                    (m) => m.message_type === Espin_Message_Type_Enum.AdminMessagesCollege,
                ) ?? []
            );
        } else if (user?.role === Espin_Role_Enum.EspinHighschoolUser) {
            // TODO - If Admin can MSG High School
            // return highSchoolMessages?.espin_message?.filter(
            //         (m) => m.message_type === Espin_Message_Type_Enum.AdminMessagesHighSchool,
            //     ) ?? []
            return [];
        }
    }, [collegeMessages?.espin_message, parentMessages?.espin_message, playerMessages?.espin_message, user?.role]);

    const groupConverationsFromData = useCallback(
        (messages: MessagesCardFragment[]) => {
            const tempConversations: Conversations = {};
            messages.forEach((c: MessagesCardFragment) => {
                // skip over admin messages
                if (
                    c.message_type !== Espin_Message_Type_Enum.AdminMessagesCollege &&
                    c.message_type !== Espin_Message_Type_Enum.AdminMessagesPlayer
                ) {
                    const conversationId = determineConversationId(user, c);
                    tempConversations[conversationId] = tempConversations[conversationId] || [];
                    tempConversations[conversationId].push(c);
                }
            });
            return tempConversations;
        },
        [user],
    );

    const groupAdminMessagesFromConversations = useCallback((conversations: Conversations) => {
        const tempAdminMessages: MessagesCardFragment[][] = [];
        Object.values(conversations).forEach((c: MessagesCardFragment[]) => {
            // checking first message type in convo should be enough to determine if it's a msg from the admins
            if (
                c[0].message_type === Espin_Message_Type_Enum.AdminMessagesCollege ||
                c[0].message_type === Espin_Message_Type_Enum.AdminMessagesPlayer
            ) {
                tempAdminMessages.push(c);
            }
        });
        return tempAdminMessages;
    }, []);

    const searchMessages = useCallback(
        async (s: string) => {
            const accessToken = await getAccessTokenSilently();
            const r = await gqlClient
                .query<SearchPlayerMessagesQuery, SearchPlayerMessagesQueryVariables>(
                    SearchPlayerMessagesDocument,
                    {
                        playerSearch: buildMessageSearchCritiria(s),
                    },
                    {
                        requestPolicy: 'network-only',
                        fetchOptions: {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                            },
                        },
                    },
                )
                .toPromise();

            const conversations = groupConverationsFromData(r.data.espin_message ?? []);
            messagesDispatch({ type: 'setGroupedConversations', conversations });
        },
        [buildMessageSearchCritiria, getAccessTokenSilently, gqlClient, groupConverationsFromData, messagesDispatch],
    );

    useEffect(() => {
        const conversations = groupConverationsFromData(messages);
        messagesDispatch({ type: 'setInitialConversationList', conversations });
        messagesDispatch({ type: 'setUserRole', userRole: user?.role });
    }, [groupConverationsFromData, messages, messagesDispatch, user?.role, groupAdminMessagesFromConversations]);

    usePageRequireAuth({});

    // effect to mark admin messages as viewed
    useEffect(() => {
        if (router.query.show_admin_messages === 'true' && !!adminMessages && adminMessages.length > 0) {
            const messageType =
                user?.role === Espin_Role_Enum.EspinCollegeUser
                    ? Espin_Message_Type_Enum.AdminMessagesCollege
                    : Espin_Message_Type_Enum.AdminMessagesPlayer;
            const shouldMarkAsViewed = adminMessages.find((m) => !m.viewed_at && m.message_type === messageType);
            if (!!shouldMarkAsViewed) {
                if (messageType === Espin_Message_Type_Enum.AdminMessagesCollege) {
                    recordAdminCollegeMessageViews(
                        {
                            collegeId: shouldMarkAsViewed.college_id,
                            messageType,
                        },
                        { additionalTypenames: ['espin_message'] },
                    );
                }
                // for parent messages, take into account selected child from dropdown
                else if (
                    !!messagesState.selectedChildId &&
                    messageType === Espin_Message_Type_Enum.AdminMessagesPlayer &&
                    user?.role === Espin_Role_Enum.EspinParentUser
                ) {
                    const tempConvoObj = adminMessages.reduce((r, a) => {
                        r[a.player_user_id] = r[a.player_user_id] || [];
                        r[a.player_user_id].push(a);
                        return r;
                    }, Object.create(null));
                    const parentShouldMarkAsViewed = tempConvoObj[messagesState.selectedChildId]
                        .filter((m) => !m.viewed_at)
                        .map((m) => m.player_user_id)
                        .filter((n, i, s) => i === s.indexOf(n));
                    if (!!parentShouldMarkAsViewed && parentShouldMarkAsViewed.length > 0) {
                        recordAdminPlayerMessageViews(
                            {
                                playerUserId: parentShouldMarkAsViewed[0],
                                messageType,
                            },
                            { additionalTypenames: ['espin_message'] },
                        );
                    }
                } else if (
                    messageType === Espin_Message_Type_Enum.AdminMessagesPlayer &&
                    user?.role === Espin_Role_Enum.EspinPlayerUser
                ) {
                    recordAdminPlayerMessageViews(
                        {
                            playerUserId: shouldMarkAsViewed.player_user_id,
                            messageType,
                        },
                        { additionalTypenames: ['espin_message'] },
                    );
                }
            }
        }
    }, [
        router.query.show_admin_messages,
        recordAdminCollegeMessageViews,
        recordAdminPlayerMessageViews,
        user?.role,
        adminMessages,
        messagesState.selectedChildId,
    ]);

    // effect to mark normal messages as viewed
    useEffect(() => {
        // if there even are conversations to view, and the user is subbed
        let conversationIndex = 0;
        if (!!router.query.college_slug && collegeConversationData?.collegeIdBySlug.length > 0) {
            conversationIndex = filteredConversationList.findIndex(
                (val) => val[0].college_id === collegeConversationData?.collegeIdBySlug[0].id,
            );
        } else if (!!router.query.player_slug && playerConversationData?.playerIdBySlug.length > 0) {
            conversationIndex = filteredConversationList.findIndex(
                (val) => val[0].player_user_id === playerConversationData?.playerIdBySlug[0].user_id,
            );
        } else if (!!router.query.high_school_slug && highSchoolConversationData?.highSchoolIdBySlug.length > 0) {
            conversationIndex = filteredConversationList.findIndex(
                (val) => val[0].high_school?.id === highSchoolConversationData?.highSchoolIdBySlug[0].id,
            );
        }
        messagesDispatch({ type: 'setSelectedIndex', index: conversationIndex });
        if (!!filteredConversationList[conversationIndex] && subscribed) {
            // record messages in the first conversation of the list as viewed
            // only do it once PLEASE
            // are there any messages here that we need to mark as viewed?
            const messageType =
                user?.role === Espin_Role_Enum.EspinCollegeUser
                    ? Espin_Message_Type_Enum.PlayerMessagesCollege
                    : Espin_Message_Type_Enum.CollegeMessagesPlayer;
            const shouldMarkAsViewed = filteredConversationList[conversationIndex].find(
                (m) => !m.viewed_at && m.message_type === messageType,
            );
            if (!!shouldMarkAsViewed) {
                recordMessageViews(
                    {
                        // just grab the user ids from the first message exchange, they will always be there
                        collegeId: shouldMarkAsViewed.college_id,
                        playerUserId: shouldMarkAsViewed.player_user_id,
                        messageType,
                    },
                    { additionalTypenames: ['espin_message'] },
                );
            }
        }
    }, [
        filteredConversationList,
        recordMessageViews,
        user?.role,
        subscribed,
        messagesDispatch,
        router,
        collegeConversationData,
        highSchoolConversationData,
        playerConversationData,
    ]);

    if (!!!user) {
        return <DashboardFullPageLoading />;
    }

    return (
        <MessagesContext.Provider
            value={{
                messagesState,
                messagesDispatch,
            }}
        >
            <DashboardLayout pageTitle="Messages">
                <NeedsOnboardingInformation>
                    {user.role === Espin_Role_Enum.EspinParentUser && <ParentMessages />}
                    <MessagesCard
                        user={user}
                        asyncMessageSearch={searchMessages}
                        initialMessageContent={initialMessageContent}
                        adminMessages={adminMessages}
                    />
                </NeedsOnboardingInformation>
            </DashboardLayout>
        </MessagesContext.Provider>
    );
}

export type MessagesReducerState = {
    selectedIndex: number;
    fullConversationList: Conversations;
    filteredConversationList: MessagesCardFragment[][];
    searchTermString: string;
    selectedChildId: string;
    userRole?: Espin_Role_Enum;
};

export type MessagesReducerAction =
    | { type: 'setSelectedIndex'; index: number }
    | { type: 'setGroupedConversations'; conversations: Conversations }
    | { type: 'setInitialConversationList'; conversations: Conversations }
    | { type: 'setSearchTermString'; searchTerm: string }
    | { type: 'setSelectedChild'; selectedChildId: string }
    | { type: 'setUserRole'; userRole: Espin_Role_Enum };

const messagesReducer = (state: MessagesReducerState, action: MessagesReducerAction): MessagesReducerState => {
    switch (action.type) {
        case 'setSelectedIndex':
            return { ...state, selectedIndex: action.index };
        case 'setUserRole':
            return { ...state, userRole: action.userRole };
        case 'setSelectedChild':
            return applyMessageFilters({ ...state, selectedChildId: action.selectedChildId });
        case 'setInitialConversationList':
            return applyMessageFilters({
                ...state,
                fullConversationList: action.conversations,
            });
        case 'setSearchTermString':
            return applyMessageFilters({ ...state, searchTermString: action.searchTerm });
    }
};

const applyMessageFilters = (state: MessagesReducerState): MessagesReducerState => {
    let filteredConversationListObj = {};

    if (state.selectedChildId != '') {
        Object.entries(state.fullConversationList).forEach((c, index) => {
            let c_id = c[0];
            let c_arr = c[1];
            filteredConversationListObj[c_id] = filteredConversationListObj[c_id] || [];
            c_arr.forEach((_c) => {
                if (_c.player_user_id === state.selectedChildId) {
                    filteredConversationListObj[c_id].push(_c);
                }
            });
        });
    } else {
        filteredConversationListObj = state.fullConversationList;
    }

    if (state.searchTermString !== '') {
        const msgKeys = Object.keys(filteredConversationListObj);
        filteredConversationListObj = {};
        msgKeys.forEach((key) => {
            const message = state.fullConversationList[key][0];
            if (state.userRole === Espin_Role_Enum.EspinCollegeUser) {
                if (
                    message.player_user.player_profile.first_name
                        .toLowerCase()
                        .includes(state.searchTermString.toLowerCase()) ||
                    message.player_user.player_profile.last_name
                        .toLowerCase()
                        .includes(state.searchTermString.toLowerCase())
                ) {
                    filteredConversationListObj[key] = state.fullConversationList[key];
                }
            } else {
                if (message.college?.name.toLowerCase().includes(state.searchTermString.toLowerCase())) {
                    filteredConversationListObj[key] = state.fullConversationList[key];
                }
            }
        });
    }

    const filteredConversationArray = Object.keys(filteredConversationListObj)
        .filter((val) => filteredConversationListObj[val].length > 0)
        .map((val) => filteredConversationListObj[val]);
    filteredConversationArray.sort((a, b) => {
        const dateA = a[a.length - 1].created_at;
        const dateB = b[b.length - 1].created_at;
        if (dateA > dateB) {
            return -1;
        } else if (dateB > dateA) {
            return 1;
        }
        return 0;
    });
    // still need to sort here
    return { ...state, filteredConversationList: filteredConversationArray };
};

type MessageContextState = {
    messagesState: MessagesReducerState;
    messagesDispatch: React.Dispatch<MessagesReducerAction>;
};

const MessagesContext = createContext<MessageContextState>({
    messagesState: {
        filteredConversationList: [],
        selectedIndex: 0,
        fullConversationList: {},
        searchTermString: '',
        selectedChildId: '',
    },
    messagesDispatch: () => {},
});

export const useMessagesContext = (): MessageContextState => useContext(MessagesContext);

function determineConversationId(user, c: MessagesCardFragment) {
    if (user?.role === Espin_Role_Enum.EspinCollegeUser) {
        if (
            c.message_type === Espin_Message_Type_Enum.CollegeMessagesHighSchool ||
            c.message_type === Espin_Message_Type_Enum.HighSchoolMessagesCollege
        ) {
            return c.high_school?.id;
        } else {
            return c.player_user_id;
        }
    }
    return c.college_id;
}
